import classNames from 'classnames';
import React, { forwardRef } from 'react';
import type { FullNamesSizingScale, GenericUIProps } from '../component.types';
import { transformSpecialCSSProperties } from '../utils';
import styles from './Text.module.scss';

export interface BaseTextProps extends GenericUIProps {
  size?: FullNamesSizingScale;
  /**
   * Boolean sets regular `text-overflow: ellipsis` on the text.
   * Pass number of lines of text to use `-webkit-line-clamp` approach.
   * @link https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp
   */
  ellipsis?: boolean | number;
  /** Apply font-weight: bold to the text */
  bold?: boolean;
  /**
   * Text color/intensity.
   * By default, no variant is set, so normal cacading styles apply. 99% of the time, that's `normal` anyway.
   */
  color?: 'normal' | 'subtle' | 'lowContrast' | 'minContrast' | 'success' | 'danger';
  /** Apply FullStory "hidden value" classes to contents */
  hideFromFullstory?: boolean;
}

function headlineSizeToTag(size: FullNamesSizingScale = 'medium') {
  const sizeMap: Record<FullNamesSizingScale, string> = {
    xlarge: 'h1',
    large: 'h2',
    medium: 'h3',
    small: 'h4',
    xsmall: 'h5',
    xxsmall: 'h6',
  };

  return sizeMap[size] || 'h3';
}

/** Common base for text components */
const BaseText = forwardRef<HTMLElement, BaseTextProps>((props, ref) => {
  const {
    className,
    ellipsis,
    color,
    style,
    size = 'medium',
    as: Component = 'span',
    bold = false,
    hideFromFullstory,
    ...finalProps
  } = transformSpecialCSSProperties(props);

  const finalStyle = typeof ellipsis !== 'number' ? style : { ...style, WebkitLineClamp: ellipsis };

  return (
    <Component
      {...finalProps}
      style={finalStyle}
      className={classNames(className, styles[`size-${size}`], {
        [styles[`color-${color}`]]: !!color,
        [styles.lineClamp]: typeof ellipsis === 'number',
        [styles.ellipsis]: typeof ellipsis === 'boolean' && ellipsis,
        [styles.bold]: bold,
        'fs-mask': hideFromFullstory,
      })}
      ref={ref}
    />
  );
});

const Display = forwardRef<HTMLElement, BaseTextProps>((props, ref) => (
  <BaseText
    as={headlineSizeToTag(props.size)}
    {...props}
    className={classNames(styles.display, props.className)}
    ref={ref}
  />
));

const Headline = forwardRef<HTMLElement, BaseTextProps>((props, ref) => (
  <BaseText
    as={headlineSizeToTag(props.size)}
    {...props}
    className={classNames(styles.headline, props.className)}
    ref={ref}
  />
));

const Paragraph = forwardRef<HTMLElement, BaseTextProps>((props, ref) => (
  <BaseText as="p" {...props} className={classNames(styles.paragraph, props.className)} ref={ref} />
));

const Label = forwardRef<HTMLElement, BaseTextProps & { htmlFor?: string }>((props, ref) => (
  <BaseText
    as="label"
    {...props}
    className={classNames(
      styles.label,
      { [styles.formInteraction]: !!props.htmlFor },
      props.className
    )}
    ref={ref}
  />
));

const Span = forwardRef<HTMLElement, BaseTextProps>((props, ref) => (
  // Re-uses .paragraph styles as they are identical, to avoid duplication in `typography.scss`
  <BaseText {...props} className={classNames(styles.paragraph, props.className)} ref={ref} />
));

export const Text = Object.assign(Span, {
  Display,
  Headline,
  Paragraph,
  Label,
});
